<template>
  <div class="create-project-container">
    <div class="bg-white border border-gray-200">
      <!-- Header -->
      <div class="bg-gray-50 border-b border-gray-200 px-4 py-3">
        <h3 class="text-sm font-semibold text-gray-700">Create New Project</h3>
      </div>

      <!-- Form Content -->
      <div class="p-4 space-y-4">
        <!-- Project Type Toggle -->
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Project Type</label>
          <div class="flex gap-2">
            <button
              type="button"
              @click="project.project_type = 'STANDARD'"
              :class="[
                'px-3 py-1.5 text-xs rounded border',
                project.project_type === 'STANDARD'
                  ? 'bg-primary-500 text-white border-orange-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              ]"
            >
              Standard
            </button>
            <button
              type="button"
              @click="project.project_type = 'ONGOING'"
              :class="[
                'px-3 py-1.5 text-xs rounded border',
                project.project_type === 'ONGOING'
                  ? 'bg-primary-500 text-white border-orange-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              ]"
            >
              Ongoing
            </button>
          </div>
          <p class="text-xs text-gray-500 mt-1">
            {{
              project.project_type === 'STANDARD'
                ? 'Fixed-scope project with defined dates. Templates and entities are locked after creation.'
                : 'Open-ended project. Entities can be added or removed at any time after creation.'
            }}
          </p>
        </div>

        <!-- Project Details Section -->
        <div class="space-y-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Project Name</label>
            <input
              v-model="project.name"
              type="text"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
              placeholder="Enter project name"
              required
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="project.description"
              rows="2"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
              placeholder="Describe the project"
              required
            />
          </div>

          <div v-if="project.project_type === 'STANDARD'" class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
              <input
                v-model="project.start_date"
                type="date"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                required
              />
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">End Date</label>
              <input
                v-model="project.end_date"
                type="date"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Owner Entity Type
              <span class="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              v-model="project.owner_entity_type"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
            >
              <option :value="null">No automatic owner</option>
              <option v-for="type in entityTypes" :key="type.id" :value="type.id">
                {{ type.type_name }}
              </option>
            </select>
            <p class="text-xs text-gray-500 mt-1">
              When set, tasks auto-assign to the user linked to an ancestor entity of this type — only if exactly one user matches.
            </p>
          </div>
        </div>

        <!-- Task Templates Section -->
        <div class="border-t border-gray-200 pt-4">
          <div class="flex justify-between items-center mb-3">
            <label class="text-xs font-medium text-gray-700">Task Templates</label>
            <button
              @click="addTask"
              class="btn-primary-sm"
            >
              + Add Task
            </button>
          </div>

          <div v-if="taskTemplates.length === 0" class="text-center py-6 text-xs text-gray-500 border border-dashed border-gray-300 rounded">
            No tasks added yet
          </div>

          <div v-else class="space-y-2">
            <div
              v-for="(task, index) in taskTemplates"
              :key="index"
              class="border border-gray-200 rounded p-2"
            >
              <div class="flex gap-2">
                <div class="shrink-0 text-xs text-gray-500 pt-1.5">
                  {{ index + 1 }}.
                </div>

                <div class="flex-1 space-y-2">
                  <div class="grid grid-cols-2 gap-2">
                    <div>
                      <input
                        v-model="task.action_name"
                        type="text"
                        class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                        placeholder="Task name"
                        required
                      />
                    </div>

                    <div>
                      <select
                        v-model="task.action_type_id"
                        class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                        required
                      >
                        <option :value="null">Select type...</option>
                        <option v-for="type in actionTypes" :key="type.id" :value="type.id">
                          {{ type.name }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <textarea
                      v-model="task.description"
                      rows="1"
                      class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                      placeholder="Task description"
                      required
                    />
                  </div>

                  <div class="grid grid-cols-2 gap-2">
                    <div>
                      <input
                        v-model.number="task.due_days_offset"
                        type="number"
                        class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                        placeholder="Due days offset"
                      />
                    </div>
                  </div>
                </div>

                <div class="shrink-0">
                  <button
                    @click="removeTask(index)"
                    class="p-1 text-gray-400 hover:text-red-600"
                    title="Remove task"
                  >
                    <font-awesome-icon :icon="['fas', 'times']" class="text-xs" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Entity Assignment Section -->
        <div class="border-t border-gray-200 pt-4">
          <label class="block text-xs font-medium text-gray-700 mb-2">
            Assign to Entities
            <span v-if="project.project_type === 'ONGOING'" class="text-gray-400 font-normal">
              (optional — entities can be added later)
            </span>
          </label>
          <div class="flex items-center gap-2">
            <EntitySearchWidget
              @entities-selected="onEntitiesSelected"
              :initial-selection="selectedEntityIds"
            />
            <span v-if="selectedEntityIds.length > 0" class="text-xs text-gray-600">
              {{ selectedEntityIds.length }} selected
            </span>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="bg-gray-50 border-t border-gray-200 px-4 py-2 flex justify-end gap-2">
        <button
          @click="cancel"
          class="btn-secondary-sm"
        >
          Cancel
        </button>
        <button
          @click="createProject"
          :disabled="!canCreate || creating"
          class="btn-primary-sm"
        >
          {{ creating ? 'Creating...' : submitLabel }}
        </button>
      </div>
    </div>

    <!-- Success/Error Messages -->
    <div v-if="successMessage" class="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
      {{ successMessage }}
    </div>
    <div v-if="errorMessage" class="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import EntitySearchWidget from '@/components/widgetcomponents/Search_Widgets/EntitySearch_V2.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'CreateProject',

  components: {
    EntitySearchWidget,
    FontAwesomeIcon
  },

  data() {
    return {
      project: {
        name: '',
        description: '',
        project_type: 'STANDARD',
        start_date: this.getTodayDate(),
        end_date: '',
        owner_entity_type: null
      },
      taskTemplates: [],
      selectedEntityIds: [],
      actionTypes: [],
      entityTypes: [],
      creating: false,
      successMessage: '',
      errorMessage: ''
    };
  },

  computed: {
    isOngoing() {
      return this.project.project_type === 'ONGOING';
    },

    submitLabel() {
      return this.isOngoing ? 'Create & Open' : 'Create Project';
    },

    canCreate() {
      const baseValid =
        this.project.name.trim() !== '' &&
        this.project.description.trim() !== '' &&
        this.taskTemplates.length > 0 &&
        this.taskTemplates.every(t =>
          t.action_name.trim() !== '' &&
          t.description.trim() !== '' &&
          t.action_type_id !== null
        );

      if (!baseValid) return false;

      if (this.isOngoing) {
        // Ongoing: dates and entities optional
        return true;
      }

      // Standard: start_date and at least one entity required
      return (
        this.project.start_date !== '' &&
        this.selectedEntityIds.length > 0
      );
    }
  },

  created() {
    this.loadActionTypes();
    this.loadEntityTypes();
  },

  methods: {
    getTodayDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },

    async loadActionTypes() {
      const response = await axios.get('api/graph-action-types/');
      this.actionTypes = response.data.results || response.data;
    },

    async loadEntityTypes() {
      const response = await axios.get('api/entity-types/');
      this.entityTypes = response.data.results || response.data;
    },

    addTask() {
      this.taskTemplates.push({
        action_name: '',
        description: '',
        action_type_id: null,
        due_days_offset: null
      });
    },

    removeTask(index) {
      this.taskTemplates.splice(index, 1);
    },

    onEntitiesSelected(entityIds) {
      this.selectedEntityIds = entityIds;
    },

    async createProject() {
      this.creating = true;
      this.successMessage = '';
      this.errorMessage = '';

      const payload = {
        name: this.project.name,
        description: this.project.description,
        project_type: this.project.project_type,
        start_date: this.isOngoing ? null : this.project.start_date,
        end_date: this.isOngoing ? null : (this.project.end_date || null),
        owner_entity_type: this.project.owner_entity_type,
        task_templates: this.taskTemplates,
        entity_ids: this.selectedEntityIds
      };

      const response = await axios.post('api/graph-projects/', payload);

      if (response.status === 201) {
        const taskCount = this.taskTemplates.length * this.selectedEntityIds.length;
        this.successMessage = this.isOngoing
          ? `Ongoing project created${taskCount > 0 ? ` with ${taskCount} initial tasks` : ''}`
          : `Project created with ${taskCount} tasks`;

        setTimeout(() => {
          this.resetForm();
          this.$emit('project-created', response.data);
        }, 1500);
      } else {
        this.errorMessage = 'Failed to create project';
      }

      this.creating = false;
    },

    resetForm() {
      this.project = {
        name: '',
        description: '',
        project_type: 'STANDARD',
        start_date: this.getTodayDate(),
        end_date: '',
        owner_entity_type: null
      };
      this.taskTemplates = [];
      this.selectedEntityIds = [];
      this.successMessage = '';
      this.errorMessage = '';
    },

    cancel() {
      this.resetForm();
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped>
.create-project-container {
  font-size: 12px;
}
</style>
